import React from 'react';
import styled, { keyframes } from 'styled-components';
import AboutUs from '../components/AboutUs';
import SEO from '../components/SEO';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandshakeAngle,
  faBookReader,
  faHeart,
  faPeopleGroup,
  faHandHoldingHeart,
  faEarthAmericas
} from '@fortawesome/free-solid-svg-icons';
import BackgroundDesign from '../components/BackgroundDesign';
import Team from '../components/Team';
import ProjectVideo from '../components/ProjectVideo';
import { useNavigate } from 'react-router-dom';


const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const AnimatedSection = styled.div`
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex ;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
     background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;
    
    filter: brightness(0.3);
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }


`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  padding: 0 2rem;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeIn} 1s ease-out forwards;
  animation-delay: 0.5s;
`;

const PageTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 4px;
  text-shadow: 2px 4px 8px rgba(0,0,0,0.4);
  
  span {
    color: ${props => props.theme.colors.primary};
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -8px;
      width: 100%;
      height: 4px;
      background: ${props => props.theme.colors.primary};
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.5s ease;
      box-shadow: 0 2px 4px rgba(153,0,17,0.3);
    }
  }
  
  &:hover span::after {
    transform: scaleX(1);
    transform-origin: left;
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
    letter-spacing: 2px;
    margin-bottom: 1rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.4rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
  color: rgba(255,255,255,0.95);
  text-shadow: 1px 2px 4px rgba(0,0,0,0.4);
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.6;
    padding: 0 1rem;
  }
`;

const HeroSection = styled.section`

  background-color:transparent;
  padding: 6rem 0;

  text-align: center;
  animation: ${fadeIn} 1s ease-out;
`;

const ServicesSection = styled.section`
  padding: 0.1rem 0;
  background: ${props => props.theme.colors.background};
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease-out;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
   justify-items: center;
`;
const LearnMoreButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.3s ease;
  
  &:hover {
    background: #A01830;
  }
`;
const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;
  animation: ${fadeIn} 1s ease-out;
  animation-delay: ${props => props.delay}s;
  opacity: 0;
  animation-fill-mode: forwards;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;
  font-size: 1.8rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const MissionSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-top: 4rem;
  position: relative;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MissionContent = styled.div`
  background: ${props => props.theme.colors.primary};
  padding: 2.5rem;
  border-radius: 10px;
  color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: ${slideIn} 1s ease-out;
  animation-delay: 0.5s;
  opacity: 0;
  animation-fill-mode: forwards;
  z-index: 2;
  position: relative;
  max-width: 500px;
`;

const MissionTitle = styled.h2`
  color: white;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const MissionText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: white;
  opacity: 0.9;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const StorySection = styled.section`
  padding: 6rem 0;
  background: ${props => props.theme.colors.background};
  position: relative;
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const StoryContent = styled.div`
  padding: 2rem;
  animation: ${fadeIn} 1s ease-out;
`;

const StoryYear = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  margin-bottom: 1rem;
`;

const StoryText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
`;

const TeamSection = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  animation: ${fadeIn} 1s ease-out;
`;

const TeamStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  text-align: center;
  padding: 2rem;
  background: ${props => props.theme.colors.background};
  border-radius: 10px;
  
  h3 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.text};
  }
`;

const ImpactGrid = styled.div`
  display: grid;
  padding: 2rem;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled(ServiceCard)`
  background: white;
  padding: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AboutUsPage = () => {
  const navigate = useNavigate();
  const [isAboutVisible, setIsAboutVisible] = React.useState(false);
  const [isServicesVisible, setIsServicesVisible] = React.useState(false);
  const [isMissionVisible, setIsMissionVisible] = React.useState(false);

  const aboutRef = React.useRef(null);
  const servicesRef = React.useRef(null);
  const missionRef = React.useRef(null);

  React.useEffect(() => {
    const createObserver = (ref, setVisible) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisible(entry.isIntersecting);
        },
        { threshold: 0.3 }
      );

      const currentRef = ref.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    };

    const cleanupAbout = createObserver(aboutRef, setIsAboutVisible);
    const cleanupServices = createObserver(servicesRef, setIsServicesVisible);
    const cleanupMission = createObserver(missionRef, setIsMissionVisible);

    return () => {
      cleanupAbout();
      cleanupServices();
      cleanupMission();
    };
  }, []);

  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="About Us | The Big Heart Association (TBH) | Casablanca, Morocco"
        description="Founded in February 2022, The Big Heart Association (TBH Morocco) unites passionate young people in Casablanca to drive education, health, and humanitarian action through solidarity and compassion."
        url="/about"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "About Us", url: "/about" }]}
        noOrganizationSchema
      />
      <MainContent>
        <PageHero>
          <PageHeroContent>
            <PageTitle>ABOUT <span>US</span></PageTitle>
            <PageSubtitle>
              Born in February 2022, united by compassion and driven by purpose -
              we are The Big Heart Association
            </PageSubtitle>
          </PageHeroContent>
        </PageHero>

        <StorySection>
          <Container>
            <StoryGrid>
              <StoryContent>
                <StoryYear>Founded in February 2022</StoryYear>
                <Title>Our Story</Title>
                <StoryText>
                  The Big Heart was founded by a group of passionate young people, united by a deep
                  desire to ignite positive change in their community. With hearts full of hope and
                  hands eager to help, we are driven by the values of solidarity, compassion, and
                  the belief that together, we can make the world a better place.
                </StoryText>
                <TeamSection>
                  <h2>Our Growing Community</h2>
                  <TeamStats>
                    <StatBox>
                      <h3>9</h3>
                      <p>Board Members</p>
                    </StatBox>
                    <StatBox>
                      <h3>70+</h3>
                      <p>Active Members</p>
                    </StatBox>
                  </TeamStats>
                </TeamSection>
              </StoryContent>
              <MissionContent>
                <MissionTitle>Our Mission</MissionTitle>
                <MissionText>
                  At The Big Heart, we believe that every small act of kindness creates ripples
                  of transformation. Our mission is to leave a lasting imprint of compassion and
                  unity, building a more inclusive and caring world for generations to come.
                </MissionText>
              </MissionContent>
            </StoryGrid>
          </Container>
        </StorySection>

        <ProjectVideo />

        <ServicesSection ref={servicesRef} className={isServicesVisible ? 'visible' : ''}>
          <Container>
            <Title>Our Impact Areas</Title>
            <Subtitle>Making a difference through focused initiatives and community care</Subtitle>
            <ImpactGrid>
              <ImpactCard delay={0.2}>
                <IconWrapper>
                  <FontAwesomeIcon icon={faHandHoldingHeart} />
                </IconWrapper>
                <h3>Humanitarian Aid</h3>
                <p>Providing food baskets, clothing, and essential support to vulnerable families</p>
              </ImpactCard>

              <ImpactCard delay={0.4}>
                <IconWrapper>
                  <FontAwesomeIcon icon={faPeopleGroup} />
                </IconWrapper>
                <h3>Community Support</h3>
                <p>Organizing visits to orphanages and retirement homes, Ramadan Iftars, and blood donation campaigns to foster community solidarity</p>
              </ImpactCard>

              <ImpactCard delay={0.6}>
                <IconWrapper>
                  <FontAwesomeIcon icon={faEarthAmericas} />
                </IconWrapper>

                <h3>Environmental Action</h3>
                <p>Leading environmental awareness campaigns and community beach clean-ups</p>
              </ImpactCard>

              <ImpactCard delay={0.6}>
                <IconWrapper>
                  <FontAwesomeIcon icon={faEarthAmericas} />
                </IconWrapper>

                <h3>Education & Renovation</h3>
                <p>Renovating schools and improving learning environments for children</p>
              </ImpactCard>
            </ImpactGrid>

            <Team />
            {/* <LearnMoreButton onClick={() => navigate('/team')}>Discover Our Team</LearnMoreButton> */}


          </Container>
        </ServicesSection>
      </MainContent>
    </PageWrapper>
  );
};

export default AboutUsPage;
